import React, { useEffect, useState } from 'react'
import translate from '../../../../core/translate'
import { cn } from 'ethcss'
import { grid } from '../../../../theme'
import styles from './telegramSettings.jcss'
import { SwitchButton } from '../../../../atoms/SwitchButton'
import QRCode from 'react-qr-code'
import Button from '../../../../blocks.simple/button/button'
import { api } from '../../../../core/api/ConnectionManager'

type User = {
    username?: string
    firstName?: string
    lastName?: string
    settings?: {
        isScreenMonitorEnabled: boolean
    }
    link?: string
}

type Group = {
    title?: string
    settings?: {
        isScreenMonitorEnabled: boolean
    }
    link?: string
}

interface IData {
    user: User
    group: Group
}

export const TelegramSettings = ({ onClose }: { onClose: () => void }) => {
    const [activeTab, setActiveTab] = useState<string>('userChat')
    const [data, setData] = useState<IData | null>(null)

    const handleSwitchChange = (field: string, instance: 'user' | 'group', value: boolean) => {
        if (!data) return

        const newData = {
            ...data,
            [instance]: {
                settings: {
                    ...data[instance].settings,
                    [field]: value,
                },
            },
        } as IData

        setData(newData)
    }

    const getBindingInfo = () => {
        api.send<object, IData>('telegramGetBindingInfo', {}).then((data) => {
            setData(data)
        })
    }

    const onSave = () => {
        api.send('telegramUpdateBindingInfo', data).then(() => {
            onClose()
        })
    }

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'telegramBindingSettingsUpdated',
            (data: any) => {
                if (data) {
                    setData(data)
                }
            },
            listenersId
        )

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [])

    useEffect(() => {
        getBindingInfo()
    }, [])

    useEffect(() => {
        const interval = setInterval(getBindingInfo, 600000)

        return () => clearInterval(interval)
    }, [])

    return (
        <div className={styles.telegramSettings}>
            <div className={styles.title}>{translate('telegramNotification')}</div>
            {data && (
                <>
                    <div className={styles.tabs}>
                        <div
                            className={cn(styles.tab, activeTab === 'userChat' ? styles.activeTab : '')}
                            onClick={() => setActiveTab('userChat')}
                        >
                            {translate('userChat')}
                        </div>
                        <div
                            className={cn(styles.tab, activeTab === 'groupChat' ? styles.activeTab : '')}
                            onClick={() => setActiveTab('groupChat')}
                        >
                            {translate('groupChat')}
                        </div>
                    </div>
                    {activeTab === 'userChat' && (
                        <>
                            {data && data.user && data.user.link === '' && (
                                <div className={styles.botUnavailable}>{translate('telegramBotUnavailable')}</div>
                            )}
                            {data && data.user && data.user.link && (
                                <div>
                                    <div className={styles.qrWrapper}>
                                        <QRCode value={data.user.link} viewBox={`0 0 160 160`} size={160} />
                                    </div>
                                    <div>
                                        {translate('scanQrOrGo')}
                                        <a href={data.user.link} target={'_blank'}>
                                            {translate('messenger')}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {data.user.settings && (
                                <div>
                                    <div className={cn(grid.mt_md)}>
                                        <div className={styles.optionCaption}>{translate('blackScreenNotify')}</div>
                                        <SwitchButton
                                            onChange={() => {
                                                if (data && data.user.settings) {
                                                    handleSwitchChange(
                                                        'isScreenMonitorEnabled',
                                                        'user',
                                                        !data.user?.settings.isScreenMonitorEnabled
                                                    )
                                                }
                                            }}
                                            checked={
                                                data &&
                                                data.user &&
                                                data.user.settings &&
                                                data.user.settings.isScreenMonitorEnabled
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    {activeTab === 'groupChat' && (
                        <>
                            {data && data.group && data.group.link === '' && (
                                <div className={styles.botUnavailable}>{translate('telegramBotUnavailable')}</div>
                            )}
                            {data && data.group && data.group.link && (
                                <div>
                                    <div className={styles.qrWrapper}>
                                        <QRCode value={data.group.link} viewBox={`0 0 160 160`} size={160} />
                                    </div>
                                    <div>
                                        {translate('scanQrOrGo')}
                                        <a href={data.group.link} target={'_blank'}>
                                            {translate('messenger')}
                                        </a>
                                    </div>
                                </div>
                            )}
                            {data.group.settings && (
                                <div>
                                    <div className={cn(grid.mt_md)}>
                                        <div className={styles.optionCaption}>{translate('blackScreenNotify')}</div>
                                        <SwitchButton
                                            onChange={() => {
                                                if (data && data.group.settings) {
                                                    handleSwitchChange(
                                                        'isScreenMonitorEnabled',
                                                        'group',
                                                        !data.group?.settings.isScreenMonitorEnabled
                                                    )
                                                }
                                            }}
                                            checked={
                                                data &&
                                                data.group &&
                                                data.group.settings &&
                                                data.group.settings.isScreenMonitorEnabled
                                            }
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    {data && data.user && data.user.settings && data.group && data.group.settings && (
                        <div className={styles.buttonWrapper}>
                            <Button mod={'fill'} onClick={onSave}>
                                {translate('save')}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
